import { blogPostSummaries } from "@/lib/blog-content"

export const DISCORD_INVITE_URL = "https://discord.gg/jZJs3Tue4S"

const navPromoPost = blogPostSummaries[0]

if (!navPromoPost?.featureImage) {
  throw new Error("The navigation promo blog post must have a feature image")
}

export type SiteNavIcon =
  | "agentic-search"
  | "cli"
  | "git-hosting"
  | "plugins"
  | "session-storage"
  | "trails"

export interface SiteNavLink {
  badge?: string
  description?: string
  href: string
  icon?: SiteNavIcon
  label: string
  external?: boolean
}

export interface SiteNavPromo {
  cta: string
  href: string
  imageAlt: string
  imageSrc: string
  imageWebpSrc?: string
  label: string
  title: string
}

export interface SiteNavGroup {
  label: string
  items: SiteNavLink[]
  menuLabel?: string
  promo?: SiteNavPromo
  /** Plain links listed under the promo in a mega menu, and after the items on mobile. */
  secondaryItems?: SiteNavLink[]
}

export type SiteNavItem = SiteNavLink | SiteNavGroup

export function isSiteNavGroup(item: SiteNavItem): item is SiteNavGroup {
  return "items" in item
}

/**
 * True when `pathname` represents the same page as `href`, including nested
 * routes (e.g. `/blog/some-post` is active for a `/blog` link). The root path
 * is matched exactly so it doesn't shadow every route.
 */
export function isActivePath(pathname: string, href: string): boolean {
  if (href === "/") return pathname === "/"
  return pathname === href || pathname.startsWith(`${href}/`)
}

/** Top-level site navigation shown in SiteHeader (desktop dropdowns + mobile accordions). */
export const siteNav: SiteNavItem[] = [
  {
    label: "Features",
    menuLabel: "Features",
    items: [
      {
        href: "/features/code-hosting",
        label: "Code Hosting",
        description: "The fastest Git forge on Earth",
        icon: "git-hosting",
      },
      {
        href: "/features/trails",
        label: "Trails",
        description: "Take work from idea to merge",
        icon: "trails",
        badge: "New",
      },
      {
        href: "/features/cli",
        label: "CLI",
        description: "Bring context wherever you work",
        icon: "cli",
      },
      {
        href: "/features/sessions",
        label: "Sessions",
        description: "Capture the why behind code",
        icon: "session-storage",
      },
      {
        href: "/blog/introducing-agentic-search-for-code-and-context",
        label: "Agentic Search",
        description: "For code and context",
        icon: "agentic-search",
      },
      {
        href: "/features/plugins",
        label: "Plugins",
        description: "Build your product on Entire",
        icon: "plugins",
      },
    ],
    promo: {
      cta: "Read →",
      href: `/blog/${navPromoPost.slug}`,
      imageAlt: navPromoPost.featureImageAlt,
      imageSrc: navPromoPost.featureImage,
      imageWebpSrc: navPromoPost.featureImageWebp,
      label: "Featured",
      title: navPromoPost.title,
    },
  },
  {
    label: "Company",
    items: [
      { href: "/about/vision", label: "Vision" },
      { href: "/about/company", label: "About" },
      { href: "https://luma.com/entire", label: "Events", external: true },
    ],
  },
  { href: "/pricing", label: "Pricing" },
  { href: "https://docs.entire.io", label: "Docs", external: true },
  { href: "/blog", label: "Blog" },
]

export const productLinks = [
  { href: "/features/trails", label: "Trails" },
  { href: "/features/cli", label: "CLI" },
  { href: "/features/sessions", label: "Sessions" },
  { href: "/features/plugins", label: "Plugins" },
  { href: "/features/code-hosting", label: "Code Hosting" },
  { href: "https://docs.entire.io", label: "Documentation" },
]

export const companyLinks = [
  { href: "/blog", label: "Blog" },
  { href: "/about/vision", label: "Vision" },
  { href: "/about/company", label: "Company" },
  { href: "/news", label: "Newsroom" },
  { href: "/brand", label: "Brand" },
  { href: "/rebellion", label: "Rebellion" },
]

export const legalLinks = [
  { href: "/privacy", label: "Privacy Policy" },
  { href: "/cookies", label: "Cookie Policy" },
  { href: "/terms", label: "Terms of Service" },
]

export const connectLinks = [
  { href: "https://github.com/entireio", label: "GitHub" },
  { href: DISCORD_INVITE_URL, label: "Discord" },
  { href: "https://x.com/goentire", label: "X" },
  { href: "https://www.instagram.com/goentire/", label: "Instagram" },
  { href: "https://www.tiktok.com/@goentire", label: "TikTok" },
  { href: "https://www.linkedin.com/company/goentire/", label: "LinkedIn" },
  { href: "https://www.youtube.com/@goentire", label: "YouTube" },
]
